import axios from "axios";
import {
    getApimAuthHeaders,
    shouldAttachApimTokenToUrl,
    EEID_HEADER,
} from "tds_shared_ui";

function resolvedAxiosUrl(config) {
    const rel = config.url || "";
    if (rel.startsWith("http")) {
        return rel;
    }
    const base = config.baseURL || "";
    if (!base) {
        return rel;
    }
    try {
        return new URL(rel, base.endsWith("/") ? base : `${base}/`).href;
    } catch {
        return rel;
    }
}

function mergeAuthHeadersInto(nextHeaders, apimHeaders, { hadAuthorization }) {
    if (!hadAuthorization && apimHeaders.Authorization) {
        nextHeaders.set("Authorization", apimHeaders.Authorization);
    }
    if (apimHeaders[EEID_HEADER] && !nextHeaders.has(EEID_HEADER)) {
        nextHeaders.set(EEID_HEADER, apimHeaders[EEID_HEADER]);
    }
}

function installAxiosApimInterceptor() {
    axios.interceptors.request.use(async (config) => {
        const url = resolvedAxiosUrl(config);
        if (!shouldAttachApimTokenToUrl(url)) {
            return config;
        }
        const nextHeaders = axios.AxiosHeaders.from(config.headers || {});
        const hadAuthorization = nextHeaders.has("Authorization");
        if (hadAuthorization) {
            config.headers = nextHeaders;
            return config;
        }
        const apimHeaders = await getApimAuthHeaders();
        mergeAuthHeadersInto(nextHeaders, apimHeaders, { hadAuthorization });
        config.headers = nextHeaders;
        return config;
    });
}

function installFetchApimInterceptor() {
    if (typeof window.fetch !== "function") {
        return;
    }
    const nativeFetch = window.fetch.bind(window);
    window.fetch = async function apimAwareFetch(input, init) {
        const url =
            typeof input === "string"
                ? input
                : input instanceof Request
                  ? input.url
                  : "";
        if (!shouldAttachApimTokenToUrl(url)) {
            return nativeFetch(input, init);
        }

        const apimHeaders = await getApimAuthHeaders();

        if (input instanceof Request) {
            const nextHeaders = new Headers(input.headers);
            if (init?.headers) {
                const initHeaders = new Headers(init.headers);
                initHeaders.forEach((value, key) => {
                    nextHeaders.set(key, value);
                });
            }
            const hadAuthorization = nextHeaders.has("Authorization");
            mergeAuthHeadersInto(nextHeaders, apimHeaders, { hadAuthorization });
            return nativeFetch(input, { ...init, headers: nextHeaders });
        }

        const nextInit = { ...init };
        const h = new Headers(init?.headers || {});
        const hadAuthorization = h.has("Authorization");
        mergeAuthHeadersInto(h, apimHeaders, { hadAuthorization });
        nextInit.headers = h;
        return nativeFetch(input, nextInit);
    };
}

export function installApimAuth() {
    if (typeof window === "undefined") {
        return;
    }
    installAxiosApimInterceptor();
    installFetchApimInterceptor();
}
